import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ClientProfileHero from "./ClientProfileHero";
import ClientMedicalSection from "./ClientMedicalSection";
import ClientNotesSection from "./ClientNotesSection";
import ClientOverviewSection from "./ClientOverviewSection";
import {
  ClientProfileCollapsible,
  ClientProfileSummaryStrip,
} from "./ClientProfileCollapsible";
import {
  ClientMembershipsSection,
  ClientAttendanceSection,
  ClientEventsSection,
} from "./ClientProfileSections";
import {
  CLIENT_PROFILE_ANCHORS,
  CLIENT_PROFILE_TAB_TO_ANCHOR,
  PATIENT_TAB_ALIASES,
} from "./patientConstants";
import {
  formatAttendanceSummary,
  formatMembershipDetail,
} from "./clientSportDisplay";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import { HubSegment } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";

const DEFAULT_TAB = "client-overview";

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function medPreview(edit) {
  const exp = edit?.medical_clearance_expires_at;
  if (!exp) return "не указана";
  const days = Math.ceil((new Date(exp).getTime() - Date.now()) / (86400000));
  if (days < 0) return "просрочена";
  if (days <= 14) return "истекает скоро";
  return "действует";
}

export default function ClientProfileSinglePage({
  patientId,
  patient,
  profile,
  edit,
  setEdit,
  sport,
  onSave,
  onArchive,
  onReload,
  writeMessage,
  subdomain,
  newPassword,
  setNewPassword,
  onSavePortalEmail,
  onSetPortalPassword,
  savingPortalEmail,
  settingPortalPassword,
  onAddToCompetition,
}) {
  const [searchParams] = useSearchParams();
  const isApp = useAdminHubUi();
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const [openId, setOpenId] = useState(null);
  const [showGuardians, setShowGuardians] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const [editingMed, setEditingMed] = useState(false);
  const didScrollTab = useRef(false);

  useAdminRefresh(onReload);

  useEffect(() => {
    setActiveTab(DEFAULT_TAB);
    setOpenId(null);
    didScrollTab.current = false;
  }, [patientId]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (!tab) return;
    const resolved = PATIENT_TAB_ALIASES[tab] || tab;
    const anchor = CLIENT_PROFILE_TAB_TO_ANCHOR[resolved] || resolved;
    if (!anchor) return;

    if (isApp) {
      setActiveTab(anchor);
      return;
    }

    if (didScrollTab.current) return;
    didScrollTab.current = true;
    setOpenId(anchor);
    requestAnimationFrame(() => scrollToSection(anchor));
  }, [searchParams, isApp]);

  const selectTab = (id) => {
    if (isApp) {
      setActiveTab(id);
      return;
    }
    setOpenId(id);
    scrollToSection(id);
  };

  const jump = (id) => selectTab(id);

  const toggleSection = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const membership = formatMembershipDetail(sport?.membership);
  const attendance = formatAttendanceSummary(
    profile?.attendance_month || sport?.attendance_month,
  );
  const groups = profile?.groups || [];
  const overviewPreview =
    groups.length > 0
      ? groups
          .slice(0, 2)
          .map((g) => g.name)
          .join(", ")
      : patient?.phone || "данные";

  const summaryItems = useMemo(
    () => [
      {
        id: "client-memberships",
        label: "Абонемент",
        value: membership.title,
        hint: membership.meta,
      },
      {
        id: "client-attendance",
        label: "Месяц",
        value: attendance.label,
        hint: attendance.hint,
      },
      {
        id: "client-overview",
        label: "Допуск",
        value: medPreview(edit),
        hint: "мед. справка",
      },
    ],
    [membership, attendance, edit],
  );

  const overviewBlock = (
    <>
      <ClientOverviewSection
        patientId={patientId}
        patient={patient}
        profile={profile}
        edit={edit}
        setEdit={setEdit}
        onSave={onSave}
        subdomain={subdomain}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSavePortalEmail={onSavePortalEmail}
        onSetPortalPassword={onSetPortalPassword}
        savingPortalEmail={savingPortalEmail}
        settingPortalPassword={settingPortalPassword}
        editingInfo={editingInfo}
        onToggleEdit={() => setEditingInfo((v) => !v)}
        onAddToCompetition={onAddToCompetition}
        compact={isApp}
      />
      {!isApp ? (
        <div className={s.clientProfileSubBlock}>
          <ClientMedicalSection
            edit={edit}
            setEdit={setEdit}
            onSave={onSave}
            editing={editingMed}
            onToggleEdit={() => setEditingMed((v) => !v)}
            embedded
          />
        </div>
      ) : null}
    </>
  );

  const tabNav = isApp ? (
    <HubSegment
      tabs={CLIENT_PROFILE_ANCHORS.map((a) => ({ id: a.id, label: a.label }))}
      active={activeTab}
      onChange={selectTab}
    />
  ) : (
    <nav
      className={s.clientProfileAnchors}
      role="tablist"
      aria-label="Разделы карточки"
    >
      {CLIENT_PROFILE_ANCHORS.map((a) => {
        const selected = openId === a.id;
        return (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={selected}
            className={
              selected
                ? `${s.clientProfileAnchor} ${s.clientProfileAnchorOn}`
                : s.clientProfileAnchor
            }
            onClick={() => selectTab(a.id)}
          >
            {a.label}
          </button>
        );
      })}
      <button
        type="button"
        role="tab"
        aria-selected={openId === "client-notes"}
        className={
          openId === "client-notes"
            ? `${s.clientProfileAnchor} ${s.clientProfileAnchorOn}`
            : s.clientProfileAnchor
        }
        onClick={() => selectTab("client-notes")}
      >
        Заметки
      </button>
    </nav>
  );

  const appTabContent = (
    <div
      className={`${aa.clientTabPanel} ${aa.clientTabPanelFlush}`}
      role="tabpanel"
      aria-label={
        CLIENT_PROFILE_ANCHORS.find((a) => a.id === activeTab)?.label
      }
    >
      {activeTab === "client-overview" ? overviewBlock : null}
      {activeTab === "client-memberships" ? (
        <div className={aa.clientSectionGroup}>
          <div className={aa.clientSectionBodyFlush}>
            <ClientMembershipsSection
              patientId={patientId}
              profile={profile}
              onChanged={onReload}
              compact
            />
          </div>
        </div>
      ) : null}
      {activeTab === "client-attendance" ? (
        <div className={aa.clientSectionGroup}>
          <div className={aa.clientSectionBodyFlush}>
            <ClientAttendanceSection patientId={patientId} />
          </div>
        </div>
      ) : null}
    </div>
  );

  const desktopSections = (
    <div className={s.clientProfileSections}>
      <ClientProfileCollapsible
        id="client-overview"
        title="Обзор"
        preview={overviewPreview}
        open={openId === "client-overview"}
        onToggle={() => toggleSection("client-overview")}
      >
        {overviewBlock}
      </ClientProfileCollapsible>

      <ClientProfileCollapsible
        id="client-memberships"
        title="Абонементы и оплаты"
        preview={membership.title}
        open={openId === "client-memberships"}
        onToggle={() => toggleSection("client-memberships")}
      >
        <ClientMembershipsSection
          patientId={patientId}
          profile={profile}
          onChanged={onReload}
          compact
        />
      </ClientProfileCollapsible>

      <ClientProfileCollapsible
        id="client-attendance"
        title="Посещаемость"
        preview={attendance.label}
        open={openId === "client-attendance"}
        onToggle={() => toggleSection("client-attendance")}
      >
        <ClientAttendanceSection patientId={patientId} />
      </ClientProfileCollapsible>

      <ClientProfileCollapsible
        id="client-events"
        title="События и опросы"
        preview="соревнования, явка"
        open={openId === "client-events"}
        onToggle={() => toggleSection("client-events")}
      >
        <ClientEventsSection patientId={patientId} profile={profile} />
      </ClientProfileCollapsible>

      <ClientProfileCollapsible
        id="client-notes"
        title="Заметки"
        preview="история тренера"
        open={openId === "client-notes"}
        onToggle={() => toggleSection("client-notes")}
      >
        <ClientNotesSection
          patientId={patientId}
          legacyNote={patient?.internal_notes}
        />
      </ClientProfileCollapsible>
    </div>
  );

  return (
    <div className={isApp ? aa.clientPage : s.clientProfilePage}>
      <ClientProfileHero
        patient={patient}
        patientId={patientId}
        sport={sport}
        edit={edit}
        onMessage={writeMessage}
        onArchive={onArchive}
        showGuardiansDetail={showGuardians}
        onToggleGuardians={() => setShowGuardians((v) => !v)}
      />

      {!isApp ? (
        <ClientProfileSummaryStrip items={summaryItems} onJump={jump} />
      ) : null}

      {tabNav}

      {isApp ? appTabContent : desktopSections}
    </div>
  );
}
